<script setup lang="ts">
  import { ref, computed, onMounted } from 'vue';
  import { useRouter } from 'vue-router';
  import TopbarOrganism from '@/components/organisms/topbar-organism/TopbarOrganism.vue';
  import InputMolecule from '@/components/molecules/input-molecule/InputMolecule.vue';
  import ButtonAtom from '@/components/atoms/ButtonAtom.vue';
  import coachService, {
    CreateCoachData,
  } from '@/services/coach/coach.service';
  import Coach from '@/models/coach/coach.model';
  import {
    SelectOption,
    transformToSelectOptions,
  } from '@/shared/selectOption';

  const router = useRouter();

  // Form data
  const formData = ref<CreateCoachData>({
    coach_name: '',
    coach_lastname: '',
    coach_email: '',
    coach_headline: '',
    coach_bio: '',
    scheduling_link: '',
    license_number: '',
    specialities_description: '',
    years_of_experience: undefined,
    client_count: '',
    price_per_hour: undefined,
    speciality_ids: [],
    free_session_scheduling_link: '',
    free_block_session_link: '',
    individual_session_link: '',
    coach_level: 1,
    is_instructor: false,
  });

  // File handling
  const coachPhoto = ref<File | null>(null);
  const coachPhotoPreview = ref<string>('');
  const photoInput = ref<HTMLInputElement | null>(null);

  // Form state
  const loading = ref(false);
  const errors = ref<Record<string, string>>({});

  // Success modal
  const showSuccessModal = ref(false);
  const authorizationUrl = ref('');
  const showCopiedMessage = ref(false);

  // Specialties
  const specialityOptions = ref<SelectOption[]>([]);
  const selectedSpecialities = ref<SelectOption[]>([]);

  // Computed for form validation
  const isFormValid = computed(() => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    const isEmailValid =
      formData.value.coach_email && emailRegex.test(formData.value.coach_email);

    return (
      formData.value.coach_name &&
      formData.value.coach_lastname &&
      isEmailValid &&
      formData.value.coach_headline &&
      coachPhoto.value
    );
  });

  // Handle file selection
  const handlePhotoChange = (event: Event) => {
    const target = event.target as HTMLInputElement;
    const file = target.files?.[0];

    if (file) {
      // Validate file type
      const allowedTypes = ['image/jpeg', 'image/png', 'image/avif'];
      if (!allowedTypes.includes(file.type)) {
        errors.value.photo =
          'Invalid file type. Please select JPEG, PNG, or AVIF image.';
        return;
      }

      // Validate file size (optional, e.g., max 5MB)
      if (file.size > 5 * 1024 * 1024) {
        errors.value.photo =
          'File size too large. Please select a file under 5MB.';
        return;
      }

      coachPhoto.value = file;
      errors.value.photo = '';

      // Create preview
      const reader = new FileReader();
      reader.onload = (e) => {
        coachPhotoPreview.value = e.target?.result as string;
      };
      reader.readAsDataURL(file);
    }
  };

  // Trigger file input
  const triggerPhotoUpload = () => {
    photoInput.value?.click();
  };

  // Email validation
  const validateEmail = () => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (
      formData.value.coach_email &&
      !emailRegex.test(formData.value.coach_email)
    ) {
      errors.value.email = 'Please enter a valid email address.';
    } else {
      errors.value.email = '';
    }
  };

  // Submit form
  const submitForm = async () => {
    validateEmail();

    if (!isFormValid.value) {
      errors.value.form =
        'Please fill in all required fields with valid information.';
      return;
    }

    loading.value = true;
    errors.value = {};

    try {
      const files = coachPhoto.value ? [coachPhoto.value] : undefined;
      const response = await coachService.create(formData.value, files);

      if (response.data) {
        // Type assertion for response data with additional fields
        const responseData = response.data as Coach & {
          coach_id?: string;
          authorization_url?: string;
        };
        if (responseData.coach_id) {
          // Show success modal with authorization URL
          authorizationUrl.value = responseData.authorization_url || '';
          showSuccessModal.value = true;
        }
      } else {
        errors.value.form = response.message || 'Failed to create coach.';
      }
    } catch (error) {
      console.error('Error creating coach:', error);
      errors.value.form = 'An error occurred while creating the coach.';
    } finally {
      loading.value = false;
    }
  };

  // Fetch specialities
  const fetchSpecialities = async () => {
    try {
      const response = await coachService.getSpecialities();
      specialityOptions.value = transformToSelectOptions(
        response.data,
        'speciality_id',
        'speciality_name',
      );
    } catch (error) {
      console.error('Error fetching specialities:', error);
      errors.value.specialities = 'Failed to load specialities.';
    }
  };

  // Add specialty
  const addSpecialty = (event: Event) => {
    const target = event.target as HTMLSelectElement;
    const specialtyId = target.value;

    if (specialtyId && selectedSpecialities.value.length < 3) {
      const specialty = specialityOptions.value.find(
        (s) => s.id === specialtyId,
      );
      if (
        specialty &&
        !selectedSpecialities.value.some((s) => s.id === specialtyId)
      ) {
        selectedSpecialities.value.push(specialty);
        formData.value.speciality_ids = selectedSpecialities.value.map(
          (s) => s.id,
        );
      }
    }
    target.value = ''; // Reset the select
  };

  // Remove specialty
  const removeSpecialty = (specialty: SelectOption) => {
    selectedSpecialities.value = selectedSpecialities.value.filter(
      (s) => s.id !== specialty.id,
    );
    formData.value.speciality_ids = selectedSpecialities.value.map((s) => s.id);
  };

  // Available specialities (excluding already selected ones)
  const availableSpecialities = computed(() => {
    return specialityOptions.value.filter(
      (option) =>
        !selectedSpecialities.value.some(
          (selected) => selected.id === option.id,
        ),
    );
  });

  // Copy URL to clipboard
  const copyAuthUrl = async () => {
    try {
      // Try modern clipboard API first
      if (navigator.clipboard && window.isSecureContext) {
        await navigator.clipboard.writeText(authorizationUrl.value);
      } else {
        // Fallback for older browsers or insecure contexts
        fallbackCopyTextToClipboard(authorizationUrl.value);
      }

      // Show copied message
      showCopiedMessage.value = true;
      setTimeout(() => {
        showCopiedMessage.value = false;
      }, 2000);
    } catch (err) {
      console.error('Failed to copy: ', err);
      // Try fallback method
      fallbackCopyTextToClipboard(authorizationUrl.value);
    }
  };

  // Fallback copy method for older browsers or insecure contexts
  const fallbackCopyTextToClipboard = (text: string) => {
    const textArea = document.createElement('textarea');
    textArea.value = text;

    // Avoid scrolling to bottom
    textArea.style.top = '0';
    textArea.style.left = '0';
    textArea.style.position = 'fixed';
    textArea.style.opacity = '0';

    document.body.appendChild(textArea);
    textArea.focus();
    textArea.select();

    try {
      const successful = document.execCommand('copy');
      if (successful) {
        // Show copied message
        showCopiedMessage.value = true;
        setTimeout(() => {
          showCopiedMessage.value = false;
        }, 2000);
      } else {
        console.error('Fallback: Unable to copy to clipboard');
      }
    } catch (err) {
      console.error('Fallback: Unable to copy to clipboard', err);
    }

    document.body.removeChild(textArea);
  };

  // Close modal and navigate to coaches list
  const closeSuccessModal = () => {
    showSuccessModal.value = false;
    router.push('/coaches-list');
  };

  // Initialize data
  onMounted(() => {
    fetchSpecialities();
  });
</script>

<template>
  <div class="coach-create">
    <TopbarOrganism
      linkText="Back to coaches"
      :showIcon="true"
      icon="angleLeft"
      primaryButtonText="Create Coach"
      route="/coachesList"
      :onPrimaryClick="submitForm"
      :primaryButtonDisabled="!isFormValid || loading"
    />

    <div class="coach-create-content">
      <div class="form-container">
        <h2>Create New Coach</h2>

        <!-- Photo Upload -->
        <div class="photo-section">
          <h3>Coach Photo *</h3>
          <div class="photo-upload">
            <input
              ref="photoInput"
              type="file"
              accept="image/jpeg,image/png,image/avif"
              @change="handlePhotoChange"
              style="display: none"
            />
            <div class="photo-preview" v-if="coachPhotoPreview">
              <img :src="coachPhotoPreview" alt="Coach photo preview" />
            </div>
            <ButtonAtom
              class="btn btn-secondary"
              @click="triggerPhotoUpload"
              :disabled="loading"
            >
              {{ coachPhotoPreview ? 'Change Photo' : 'Upload Photo' }}
            </ButtonAtom>
          </div>
          <p v-if="errors.photo" class="error">{{ errors.photo }}</p>
        </div>

        <!-- Basic Info -->
        <div class="form-section">
          <h3>Basic Information</h3>
          <div class="form-row">
            <InputMolecule
              title="First Name*"
              label="Coach's first name"
              id="coach_name"
              placeholder="Enter first name"
              v-model="formData.coach_name"
            />
            <InputMolecule
              title="Last Name*"
              label="Coach's last name"
              id="coach_lastname"
              placeholder="Enter last name"
              v-model="formData.coach_lastname"
            />
          </div>
          <InputMolecule
            title="Email*"
            label="Coach's email address"
            id="coach_email"
            placeholder="Enter email address"
            type="email"
            v-model="formData.coach_email"
            @blur="validateEmail"
          />
          <p v-if="errors.email" class="error">{{ errors.email }}</p>
          <InputMolecule
            title="Headline*"
            label="Professional headline"
            id="coach_headline"
            placeholder="e.g., Certified Life Coach & Mental Health Professional"
            v-model="formData.coach_headline"
          />
          <div class="form-group">
            <h4>Bio</h4>
            <label for="coach_bio">Coach's biography</label>
            <textarea
              id="coach_bio"
              v-model="formData.coach_bio"
              placeholder="Tell us about the coach's background and expertise..."
              rows="4"
              class="textarea"
            ></textarea>
          </div>
        </div>

        <!-- Professional Info -->
        <div class="form-section">
          <h3>Professional Information</h3>
          <!-- <InputMolecule
            title="License Number"
            label="Professional license number"
            id="license_number"
            placeholder="Enter license number"
            v-model="formData.license_number"
          /> -->
          <div class="form-row">
            <InputMolecule
              title="Years of Experience"
              label="Years of professional experience"
              id="years_of_experience"
              placeholder="e.g., 5"
              type="number"
              :modelValue="formData.years_of_experience?.toString() || ''"
              @update:modelValue="
                formData.years_of_experience = $event
                  ? Number($event)
                  : undefined
              "
            />
            <InputMolecule
              title="Client Count"
              label="Number of clients served"
              id="client_count"
              placeholder="e.g., 100+"
              v-model="formData.client_count"
            />
          </div>
          <div class="form-row">
            <InputMolecule
              title="Price per Hour"
              label="Hourly rate in USD"
              id="price_per_hour"
              placeholder="e.g., 150"
              type="number"
              :modelValue="formData.price_per_hour?.toString() || ''"
              @update:modelValue="
                formData.price_per_hour = $event ? Number($event) : undefined
              "
            />
            <div class="form-group">
              <h4>Coach Level</h4>
              <label for="coach_level">Select coach experience level</label>
              <select
                id="coach_level"
                v-model="formData.coach_level"
                class="select-input"
              >
                <option value="1">Level 1</option>
                <option value="2">Level 2</option>
                <option value="3">Level 3</option>
              </select>
            </div>
          </div>
          <div class="form-group checkbox-group">
            <label class="checkbox-label">
              <input
                type="checkbox"
                v-model="formData.is_instructor"
                class="checkbox-input"
              />
              <span class="checkbox-text">Is Instructor</span>
            </label>
            <p class="checkbox-description">
              Check if this coach is also an instructor
            </p>
          </div>
        </div>

        <!-- Specialties -->
        <div class="form-section">
          <h3>Specialties</h3>
          <div class="form-group">
            <h4>Specialties Description</h4>
            <label for="specialities_description"
              >Description of specialties</label
            >
            <textarea
              id="specialities_description"
              v-model="formData.specialities_description"
              placeholder="Describe the coach's areas of specialization..."
              rows="3"
              class="textarea"
            ></textarea>
          </div>
          <div class="form-group">
            <h4>Specialties</h4>
            <label for="speciality_ids"
              >Select coach specialties (maximum 3)</label
            >
            <div class="specialty-dropdown">
              <div
                class="specialty-tags"
                v-if="selectedSpecialities.length > 0"
              >
                <div
                  v-for="specialty in selectedSpecialities"
                  :key="specialty.id"
                  class="specialty-tag"
                >
                  {{ specialty.value }}
                  <span class="remove-tag" @click="removeSpecialty(specialty)"
                    >×</span
                  >
                </div>
              </div>
              <select
                v-if="selectedSpecialities.length < 3"
                @change="addSpecialty($event)"
                class="specialty-select"
              >
                <option value="">Choose a specialty...</option>
                <option
                  v-for="option in availableSpecialities"
                  :key="option.id"
                  :value="option.id"
                >
                  {{ option.value }}
                </option>
              </select>
            </div>
            <p v-if="errors.specialities" class="error">
              {{ errors.specialities }}
            </p>
          </div>
        </div>

        <!-- Contact Info -->
        <div class="form-section">
          <h3>Contact Information</h3>
          <InputMolecule
            title="Scheduling Link"
            label="Link for booking first appointment of block sessions (paid event)"
            id="scheduling_link"
            placeholder="https://calendly.com/coach-name"
            v-model="formData.scheduling_link"
          />
          <InputMolecule
            title="Free Block Scheduling Link"
            label="Link for booking free block session (3 sessions)"
            id="free_block_scheduling_link"
            placeholder="https://calendly.com/coach-name"
            v-model="formData.free_block_session_link"
          />
          <InputMolecule
            title="Individual Session Link"
            label="Link for booking individual session"
            id="individual_session_link"
            placeholder="https://calendly.com/coach-name"
            v-model="formData.individual_session_link"
          />
          <InputMolecule
            title="Free Session Scheduling Link"
            label="Link for booking free sessions"
            id="free_session_scheduling_link"
            placeholder="https://calendly.com/coach-name"
            v-model="formData.free_session_scheduling_link"
          />
        </div>

        <!-- Error Display -->
        <div v-if="errors.form" class="error-section">
          <p class="error">{{ errors.form }}</p>
        </div>

        <!-- Submit Button -->
        <div class="submit-section">
          <ButtonAtom
            class="btn btn-primary"
            @click="submitForm"
            :disabled="!isFormValid || loading"
          >
            {{ loading ? 'Creating...' : 'Create Coach' }}
          </ButtonAtom>
        </div>
      </div>
    </div>

    <!-- Success Modal -->
    <div
      v-if="showSuccessModal"
      class="modal-overlay"
      @click="closeSuccessModal"
    >
      <div class="modal-content" @click.stop>
        <div class="modal-header">
          <h3>Coach Created Successfully!</h3>
          <button class="close-btn" @click="closeSuccessModal">&times;</button>
        </div>
        <div class="modal-body">
          <p class="success-message">
            The coach profile has been created successfully. Please send this
            authorization link to the coach to allow our application to manage
            their meetings.
          </p>
          <div class="auth-url-section">
            <label for="auth-url">Authorization URL:</label>
            <div class="url-input-container">
              <input
                id="auth-url"
                type="text"
                :value="authorizationUrl"
                readonly
                class="auth-url-input"
              />
              <ButtonAtom
                :class="`btn ${
                  showCopiedMessage ? 'btn-success' : 'btn-secondary'
                } copy-btn`"
                @click="copyAuthUrl"
                :disabled="false"
              >
                {{ showCopiedMessage ? 'Copied!' : 'Copy' }}
              </ButtonAtom>
            </div>
          </div>
        </div>
        <div class="modal-footer">
          <ButtonAtom
            class="btn btn-primary"
            @click="closeSuccessModal"
            :disabled="false"
          >
            Continue to Coaches List
          </ButtonAtom>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>
  .coach-create {
    display: flex;
    flex-direction: column;
    height: 100%;
  }

  .coach-create-content {
    flex: 1;
    padding: 24px;
    overflow-y: auto;
    background-color: #fff;
  }

  .form-container {
    max-width: 800px;
    margin: 0 auto;
  }

  .form-container h2 {
    margin-bottom: 32px;
    color: #2c3e50;
    font-size: 28px;
    font-weight: 600;
  }

  .form-section {
    margin-bottom: 32px;
    padding: 24px;
    border: 1px solid #e1e5e9;
    border-radius: 12px;
    background-color: #fafafa;
  }

  .form-section h3 {
    margin-bottom: 20px;
    color: #2c3e50;
    font-size: 20px;
    font-weight: 600;
  }

  .photo-section {
    margin-bottom: 32px;
    padding: 24px;
    border: 1px solid #e1e5e9;
    border-radius: 12px;
    background-color: #fafafa;
  }

  .photo-section h3 {
    margin-bottom: 16px;
    color: #2c3e50;
    font-size: 20px;
    font-weight: 600;
  }

  .photo-upload {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 16px;
  }

  .photo-preview {
    width: 120px;
    height: 120px;
    border-radius: 50%;
    overflow: hidden;
    border: 3px solid #e1e5e9;
  }

  .photo-preview img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }

  .form-row {
    grid-template-columns: 1fr 1fr;
    gap: 20px;
  }

  .form-group {
    margin-bottom: 20px;
  }

  .form-group h4 {
    margin-bottom: 8px;
    color: #2c3e50;
    font-size: 16px;
    font-weight: 600;
  }

  .form-group label {
    display: block;
    margin-bottom: 8px;
    color: #6b7280;
    font-size: 14px;
  }

  .textarea {
    width: 100%;
    padding: 12px 16px;
    border: none;
    border-radius: 8px;
    background-color: var(--color-gray-30);
    resize: vertical;
    font-family: inherit;
    font-size: 14px;
    line-height: 1.5;
  }

  .textarea:focus {
    outline: none;
    box-shadow: 0 0 0 2px rgba(66, 185, 131, 0.2);
  }

  .error {
    color: #dc2626;
    font-size: 14px;
    margin-top: 4px;
  }

  .error-section {
    margin: 20px 0;
    padding: 16px;
    background-color: #fef2f2;
    border: 1px solid #fecaca;
    border-radius: 8px;
  }

  .submit-section {
    margin-top: 32px;
    text-align: center;
  }

  .specialty-dropdown {
    margin-bottom: 16px;
  }

  .specialty-tags {
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
    margin-bottom: 12px;
  }

  .specialty-tag {
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 8px 12px;
    background-color: #42b983;
    color: white;
    border-radius: 20px;
    font-size: 14px;
  }

  .remove-tag {
    cursor: pointer;
    font-weight: bold;
    width: 24px;
    height: 24px;
    font-size: 16px;
    border-radius: 50%;
    background-color: rgba(255, 255, 255, 0.2);
    transition: background-color 0.2s;
  }

  .remove-tag:hover {
    background-color: rgba(255, 255, 255, 0.3);
  }

  .specialty-select {
    width: 100%;
    padding: 12px 16px;
    border: none;
    border-radius: 8px;
    background-color: var(--color-gray-30);
    font-family: inherit;
    font-size: 14px;
    cursor: pointer;
  }

  .specialty-select:focus {
    outline: none;
    box-shadow: 0 0 0 2px rgba(66, 185, 131, 0.2);
  }

  .select-input {
    width: 100%;
    padding: 12px 16px;
    border: none;
    border-radius: 8px;
    background-color: var(--color-gray-30);
    font-family: inherit;
    font-size: 14px;
    cursor: pointer;
  }

  .select-input:focus {
    outline: none;
    box-shadow: 0 0 0 2px rgba(66, 185, 131, 0.2);
  }

  .checkbox-group {
    margin-bottom: 0;
  }

  .checkbox-label {
    display: flex;
    align-items: center;
    gap: 12px;
    cursor: pointer;
    margin-bottom: 4px;
  }

  .checkbox-input {
    width: 18px;
    height: 18px;
    accent-color: #42b983;
    cursor: pointer;
  }

  .checkbox-text {
    color: #2c3e50;
    font-size: 16px;
    font-weight: 600;
  }

  .checkbox-description {
    color: #6b7280;
    font-size: 14px;
    margin: 0;
    margin-left: 30px;
  }

  /* Modal Styles */
  .modal-overlay {
    position: fixed;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background-color: rgba(0, 0, 0, 0.5);
    display: flex;
    justify-content: center;
    align-items: center;
    z-index: 1000;
  }

  .modal-content {
    background: white;
    border-radius: 12px;
    width: 90%;
    max-width: 600px;
    max-height: 80vh;
    overflow-y: auto;
    box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.1),
      0 10px 10px -5px rgba(0, 0, 0, 0.04);
  }

  .modal-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 24px 24px 0 24px;
    border-bottom: 1px solid #e1e5e9;
  }

  .modal-header h3 {
    margin: 0;
    color: #2c3e50;
    font-size: 24px;
    font-weight: 600;
  }

  .close-btn {
    background: none;
    border: none;
    font-size: 28px;
    color: #6b7280;
    cursor: pointer;
    padding: 0;
    width: 32px;
    height: 32px;
    display: flex;
    align-items: center;
    justify-content: center;
    border-radius: 50%;
    transition: background-color 0.2s;
  }

  .close-btn:hover {
    background-color: #f3f4f6;
  }

  .modal-body {
    padding: 24px;
  }

  .success-message {
    color: #374151;
    font-size: 16px;
    line-height: 1.6;
    margin-bottom: 24px;
  }

  .auth-url-section {
    margin-bottom: 16px;
  }

  .auth-url-section label {
    display: block;
    font-weight: 600;
    color: #2c3e50;
    margin-bottom: 8px;
    font-size: 14px;
  }

  .url-input-container {
    display: flex;
    gap: 12px;
    align-items: center;
  }

  .auth-url-input {
    flex: 1;
    padding: 12px 16px;
    border: 1px solid #d1d5db;
    border-radius: 8px;
    font-size: 14px;
    background-color: #f9fafb;
    color: #374151;
    font-family: monospace;
  }

  .auth-url-input:focus {
    outline: none;
    border-color: #42b983;
    box-shadow: 0 0 0 3px rgba(66, 185, 131, 0.1);
  }

  .copy-btn {
    white-space: nowrap;
    transition: background-color 0.3s ease, color 0.3s ease;
  }

  .btn-success {
    background-color: #10b981 !important;
    color: white !important;
  }

  .btn-success:hover {
    background-color: #059669 !important;
  }

  .modal-footer {
    padding: 0 24px 24px 24px;
    display: flex;
    justify-content: flex-end;
    border-top: 1px solid #e1e5e9;
    padding-top: 16px;
  }

  @media (max-width: 768px) {
    .form-row {
      grid-template-columns: 1fr;
    }

    .coach-create-content {
      padding: 16px;
    }
  }
</style>
